import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Modal from '../Modal';
import { contractorAuth } from '../../lib/contractorAuth';
import { holderLabel, tagText, deviceName } from '../../lib/assets';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/** Whether a category (by name) belongs to a desk by default (108: monitors, docks, and what IT ticks). */
export const deskDefault = (meta, categoryName) => Boolean((meta?.categories || [])
  .find((c) => c.name === categoryName)?.desk_default);

/** Places a desk may go under: active, not storage, not another desk. */
const deskParents = (meta) => (meta?.locations || []).filter((l) => l.active
  && !['storage', 'container', 'shelf', 'desk'].includes(l.kind));

/**
 * "MAKE THIS A DESK", from a laptop's page (108). Gordon, 28 October: "we
 * are using the users' laptops to identify the assets on the workstation".
 * The desk is made at the laptop's place, for the laptop's person (fixed)
 * or nobody in particular (hot); the screens and docks plugged into the
 * laptop move onto it - unplugged, held by nobody, the desk's kit - and the
 * page asks about everything else plugged in (a headset is usually the
 * person's), unticked.
 */
export function MakeDeskDialog({ asset, items = [], meta, onClose, onDone }) {
  const plugged = items.filter((c) => !['retired', 'disposed'].includes(c.lifecycle_status));
  const [form, setForm] = useState({
    name: '', parent_id: asset.location_id || '',
    who: asset.assigned_user_id ? `u:${asset.assigned_user_id}` : 'hot'
  });
  const [ticked, setTicked] = useState(() => new Set(plugged.filter((c) => deskDefault(meta, c.category_name)).map((c) => c.id)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (id) => setTicked((t) => { const n = new Set(t); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const who = form.who === 'hot' ? { desk_mode: 'hot' }
        : form.who === 'unassigned' ? { desk_mode: 'fixed', user_id: null } : { user_id: form.who.slice(2) };
      const r = await axios.post(`${API_BASE}/assets/${asset.id}/make-desk`,
        { name: form.name.trim(), parent_id: form.parent_id || undefined, asset_ids: [...ticked], ...who }, contractorAuth());
      onDone(r.data);
    } catch (err) {
      setError(err.response?.data?.error || 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Make a desk — ${tagText(asset)}`} onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" form="as-desk-make" className="btn btn-primary" disabled={busy || !form.name.trim()}>
            {busy ? 'Saving…' : 'Make the desk'}
          </button>
        </>
      )}>
      <form id="as-desk-make" onSubmit={submit} data-testid="make-desk-form">
        {error && <p className="as-error" role="alert">{error}</p>}
        <p className="as-quiet">
          The desk is a place. What stays at it - its screens and dock - becomes the desk’s, so whoever sits there
          next uses them without anything being handed over.
        </p>
        <div className="form-group">
          <label htmlFor="as-desk-name">Desk name</label>
          <input id="as-desk-name" value={form.name} onChange={set('name')} placeholder="Desk 2.14" maxLength={120} required />
        </div>
        <div className="form-group">
          <label htmlFor="as-desk-parent">In</label>
          <select id="as-desk-parent" value={form.parent_id} onChange={set('parent_id')} required>
            <option value="">Choose the room or floor…</option>
            {deskParents(meta).map((l) => <option key={l.id} value={l.id}>{l.path}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="as-desk-who">Whose desk</label>
          <select id="as-desk-who" value={form.who} onChange={set('who')}>
            <option value="hot">Hot desk — whoever sits there</option>
            <option value="unassigned">Fixed, not given to anybody yet</option>
            {(meta?.users || []).map((u) => <option key={u.id} value={`u:${u.id}`}>{`${u.display_name || u.email}’s desk`}</option>)}
          </select>
        </div>
        {plugged.length > 0 && (
          <fieldset className="as-desk-plugged">
            <legend>Plugged into it now — which stay at the desk?</legend>
            {plugged.map((c) => (
              <label key={c.id} className="as-check">
                <input type="checkbox" checked={ticked.has(c.id)} onChange={() => toggle(c.id)} />
                {`${c.category_name} · ${tagText(c)}${deviceName(c) !== c.category_name ? ` · ${deviceName(c)}` : ''}`}
                {!deskDefault(meta, c.category_name) && <span className="as-quiet"> — usually the person’s</span>}
              </label>
            ))}
          </fieldset>
        )}
      </form>
    </Modal>
  );
}

/**
 * "PUT ON A DESK" for any device (108): moved to the desk, unplugged from
 * whatever it was in. A monitor or dock becomes the desk's (held by
 * nobody); anything else stays its holder's unless unticked - the
 * per-device override, both ways.
 */
export function PutOnDeskDialog({ asset, meta, onClose, onDone }) {
  const [desks, setDesks] = useState(null);
  const [deskId, setDeskId] = useState('');
  const held = Boolean(asset.holder_name);
  const [keep, setKeep] = useState(held && !deskDefault(meta, asset.category_name));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    axios.get(`${API_BASE}/assets/desks`, contractorAuth())
      .then((r) => { if (live) setDesks(r.data?.desks || []); })
      .catch((e) => { if (live) { setDesks([]); setError(e.response?.data?.error || 'Could not load the desks.'); } });
    return () => { live = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await axios.post(`${API_BASE}/assets/${asset.id}/put-on-desk`, { desk_id: deskId, keep_holder: held && keep }, contractorAuth());
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Put on a desk — ${tagText(asset)}`} onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" form="as-desk-put" className="btn btn-primary" disabled={busy || !deskId}>
            {busy ? 'Saving…' : 'Put it there'}
          </button>
        </>
      )}>
      <form id="as-desk-put" onSubmit={submit}>
        {error && <p className="as-error" role="alert">{error}</p>}
        <div className="form-group">
          <label htmlFor="as-desk-pick">Desk</label>
          <select id="as-desk-pick" value={deskId} onChange={(e) => setDeskId(e.target.value)} required disabled={!desks}>
            <option value="">{desks === null ? 'Loading…' : desks.length ? 'Choose a desk…' : 'No desks yet — add one from the map'}</option>
            {(desks || []).map((d) => <option key={d.id} value={d.id}>{`${d.label} — ${d.path}`}</option>)}
          </select>
        </div>
        {held && (
          <label className="as-check">
            <input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} />
            {`Keep it ${holderLabel(asset)}’s (otherwise it becomes the desk’s)`}
          </label>
        )}
      </form>
    </Modal>
  );
}
